import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageContentCardSx,
  pageHeroShellSx,
} from "../../styles/pageModernStyles";
import {
  createMeetingSession,
  deleteMeetingSession,
  listMeetingSessions,
} from "../../api/Meetings/meetingSessionsApi";
import MeetingBrandSelect from "./MeetingBrandSelect";
import MeetingAttendeeSelect from "./MeetingAttendeeSelect";
import MeetingClientContactSelect from "./MeetingClientContactSelect";
import MeetingDeleteDialog from "./MeetingDeleteDialog";
import MeetingConsentPanel from "./MeetingConsentPanel";
import {
  meetingChipRowSx,
  meetingListPaneSx,
  meetingListSplitSx,
  meetingPageShellSx,
  meetingPreviewPaneSx,
  meetingStickyHeaderSx,
} from "./meetingUiStyles";
import {
  canDeleteMeetingFromToken,
  formatMeetingMediaMaxMb,
  formatMeetingMediaWarnMb,
  hasMeetingConsent,
  isMeetingConsentComplete,
  MEETING_MEDIA_RETENTION_DAYS,
  mediaDaysLeftFromSession,
  statusLabelKey,
  toDateInputValue,
} from "../../utils/meetingRecorder";
import {
  filterMeetingSessions,
  formatMeetingListDate,
  groupMeetingsByCustomer,
  meetingPreviewHeadline,
  uniqueMeetingCustomers,
} from "../../utils/meetingListUtils";
import { jwtDecode } from "jwt-decode";

const emptyConsent = () => ({
  consentClientsInformed: false,
  consentRecordingAccepted: false,
  consentAiAccepted: false,
});

const statusColor = {
  recording: "warning",
  processing: "info",
  ready: "success",
  failed: "error",
};

function currentTokenPayload() {
  try {
    return jwtDecode(localStorage.getItem("token") || "");
  } catch {
    return {};
  }
}

function currentUserId() {
  const payload = currentTokenPayload();
  const n = Number(payload.userId ?? payload.id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function statusDotColor(theme, status) {
  const key = statusColor[status];
  if (key && theme.palette[key]?.main) return theme.palette[key].main;
  return theme.palette.text.disabled;
}

function MeetingMediaCountChip({ icon, count, color, title }) {
  return (
    <Chip
      size="small"
      icon={icon}
      label={String(count)}
      title={title}
      sx={{
        height: 22,
        flexShrink: 0,
        borderColor: alpha(color, 0.45),
        bgcolor: alpha(color, 0.12),
        color,
        "& .MuiChip-icon": {
          color,
          ml: 0.5,
          mr: -0.25,
          fontSize: 14,
        },
        "& .MuiChip-label": {
          px: 0.6,
          fontSize: "0.72rem",
          fontWeight: 700,
          lineHeight: 1.2,
        },
      }}
      variant="outlined"
    />
  );
}

/** Fila compacta del árbol: título · fecha ES + 3 chips media. */
function MeetingTreeLeaf({
  row,
  selected,
  t,
  theme,
  locale,
  onSelect,
  onOpen,
}) {
  const dateLabel = formatMeetingListDate(row.meetingDate, locale) || "—";
  const audio = row.audioCount || 0;
  const photos = row.imageCount ?? row.photoCount ?? 0;
  const videos = row.videoCount || 0;
  const audioColor = theme.palette.warning.dark;
  const photoColor = theme.palette.primary.main;
  const videoColor = theme.palette.secondary.main;
  return (
    <Box
      role="treeitem"
      aria-selected={selected}
      onClick={() => onSelect(row)}
      onDoubleClick={() => onOpen(row)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 40,
        pl: 1.25,
        pr: 1,
        py: 0.45,
        cursor: "pointer",
        borderRadius: 1.25,
        borderLeft: "3px solid",
        borderColor: selected ? "primary.main" : "transparent",
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.1)
          : "transparent",
        "&:hover": {
          bgcolor: selected
            ? alpha(theme.palette.primary.main, 0.14)
            : alpha(theme.palette.action.hover, 0.6),
        },
      }}
    >
      <Box
        aria-hidden
        title={t(statusLabelKey(row.status))}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: statusDotColor(theme, row.status),
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            fontWeight: selected ? 700 : 600,
            fontSize: "0.875rem",
            lineHeight: 1.25,
          }}
        >
          {row.title || t("Meetings.untitled")}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.6}
          sx={{ mt: 0.2, minWidth: 0, width: "100%" }}
        >
          <Typography
            noWrap
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0, lineHeight: 1.2, fontWeight: 600 }}
          >
            {dateLabel}
            {!hasMeetingConsent(row)
              ? ` · ${t("Meetings.consentMissingChip")}`
              : ""}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 4 }} />
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexShrink: 0 }}
          >
            <MeetingMediaCountChip
              color={audioColor}
              count={audio}
              title={t("Meetings.record")}
              icon={<MicNoneOutlinedIcon />}
            />
            <MeetingMediaCountChip
              color={photoColor}
              count={photos}
              title={t("Meetings.photo")}
              icon={<PhotoCameraOutlinedIcon />}
            />
            <MeetingMediaCountChip
              color={videoColor}
              count={videos}
              title={t("Meetings.video")}
              icon={<VideocamOutlinedIcon />}
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

function MeetingTreeGroup({
  group,
  expanded,
  onToggle,
  selectedId,
  t,
  theme,
  locale,
  onSelect,
  onOpen,
}) {
  const label = group.customerName || t("Meetings.noCustomer");
  const count = group.meetings.length;
  return (
    <Box role="treeitem" aria-expanded={expanded} sx={{ minWidth: 0 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          minHeight: 40,
          px: 0.75,
          py: 0.35,
          cursor: "pointer",
          borderRadius: 1.25,
          userSelect: "none",
          "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.7) },
        }}
      >
        {expanded ? (
          <ExpandMoreOutlinedIcon
            sx={{ fontSize: 20, color: "text.secondary" }}
          />
        ) : (
          <ChevronRightOutlinedIcon
            sx={{ fontSize: 20, color: "text.secondary" }}
          />
        )}
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 800,
            fontSize: "0.8125rem",
            letterSpacing: 0.01,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            minWidth: 22,
            textAlign: "right",
          }}
        >
          {count}
        </Typography>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Stack
          role="group"
          spacing={0.15}
          sx={{
            pb: 0.5,
            borderLeft: "1px solid",
            borderColor: "divider",
            ml: 1.5,
          }}
        >
          {group.meetings.map((row) => (
            <MeetingTreeLeaf
              key={row.id}
              row={row}
              selected={Number(selectedId) === Number(row.id)}
              t={t}
              theme={theme}
              locale={locale}
              onSelect={onSelect}
              onOpen={onOpen}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

export default function MeetingsPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dateLocale = i18n.language || "es-ES";
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [meetingDate, setMeetingDate] = useState(() => toDateInputValue());
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [clientContactIds, setClientContactIds] = useState([]);
  const [consent, setConsent] = useState(emptyConsent);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [query, setQuery] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  /** Claves de cliente expandidas en el árbol ("" = sin cliente). */
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const canDelete = useMemo(
    () => canDeleteMeetingFromToken(currentTokenPayload()),
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listMeetingSessions();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setSelectedId((prev) => {
        if (prev && list.some((r) => Number(r.id) === Number(prev)))
          return prev;
        return list[0]?.id ?? null;
      });
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.loadError"),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const customers = useMemo(() => uniqueMeetingCustomers(rows), [rows]);
  const filtered = useMemo(
    () =>
      filterMeetingSessions(rows, {
        query,
        customer: customerFilter,
        status: statusFilter,
      }),
    [rows, query, customerFilter, statusFilter],
  );
  const groups = useMemo(() => groupMeetingsByCustomer(filtered), [filtered]);
  const selected = useMemo(
    () => filtered.find((r) => Number(r.id) === Number(selectedId)) || null,
    [filtered, selectedId],
  );

  useEffect(() => {
    if (!filtered.length) {
      setSelectedId(null);
      return;
    }
    if (!filtered.some((r) => Number(r.id) === Number(selectedId))) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  // Al seleccionar (o filtrar), abrir el nodo del cliente de la reunión activa
  useEffect(() => {
    if (!selected) return;
    const key = String(selected.customerName || "").trim();
    setExpandedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, [selected]);

  // Con búsqueda / filtro de cliente: expandir todos los grupos visibles
  useEffect(() => {
    const forceOpen = Boolean(query.trim() || customerFilter);
    if (!forceOpen || !groups.length) return;
    setExpandedKeys(new Set(groups.map((g) => g.customerName)));
  }, [query, customerFilter, groups]);

  const toggleGroup = useCallback((customerName) => {
    const key = String(customerName || "").trim();
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const openCreate = () => {
    const uid = currentUserId();
    setTitle("");
    setCustomerName("");
    setMeetingDate(toDateInputValue());
    setAttendeeIds(uid ? [uid] : []);
    setClientContactIds([]);
    setConsent(emptyConsent());
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!isMeetingConsentComplete(consent)) {
      setError(t("Meetings.consentRequired"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const session = await createMeetingSession({
        title: title.trim(),
        customerName: customerName.trim(),
        meetingDate,
        attendeeUserIds: attendeeIds,
        clientContactIds,
        ...consent,
      });
      setOpen(false);
      setTitle("");
      setCustomerName("");
      setConsent(emptyConsent());
      navigate(`/reuniones/${session.id}`);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.createError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setError("");
    try {
      await deleteMeetingSession(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.deleteError"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const openMeeting = (row) => {
    if (row?.id) navigate(`/reuniones/${row.id}`);
  };

  const selectMeeting = (row) => {
    setSelectedId(row.id);
    if (!isDesktop) openMeeting(row);
  };

  const previewHeadline = meetingPreviewHeadline(selected);
  const previewDays = selected ? mediaDaysLeftFromSession(selected) : null;

  const filtersBar = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      sx={{
        mb: 0,
        minWidth: 0,
        pt: 1.25,
        px: 0.25,
      }}
    >
      <TextField
        size="small"
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("Meetings.searchPlaceholder")}
        InputProps={{
          startAdornment: (
            <SearchOutlinedIcon
              fontSize="small"
              sx={{ mr: 1, color: "text.secondary" }}
            />
          ),
        }}
      />
      <FormControl
        size="small"
        sx={{ minWidth: { sm: 180 }, width: { xs: "100%", sm: 200 } }}
      >
        <InputLabel id="meeting-filter-customer">
          {t("Meetings.filterCustomer")}
        </InputLabel>
        <Select
          labelId="meeting-filter-customer"
          label={t("Meetings.filterCustomer")}
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
        >
          <MenuItem value="">{t("Meetings.filterAll")}</MenuItem>
          {customers.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        sx={{ minWidth: { sm: 140 }, width: { xs: "100%", sm: 160 } }}
      >
        <InputLabel id="meeting-filter-status">
          {t("Meetings.filterStatus")}
        </InputLabel>
        <Select
          labelId="meeting-filter-status"
          label={t("Meetings.filterStatus")}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="">{t("Meetings.filterAll")}</MenuItem>
          <MenuItem value="recording">{t("Meetings.statusRecording")}</MenuItem>
          <MenuItem value="processing">
            {t("Meetings.statusProcessing")}
          </MenuItem>
          <MenuItem value="ready">{t("Meetings.statusReady")}</MenuItem>
          <MenuItem value="failed">{t("Meetings.statusFailed")}</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  const listPane = (
    <Paper
      role="tree"
      elevation={0}
      sx={{
        ...meetingListPaneSx,
        ...pageContentCardSx(theme),
        gap: 0.25,
        py: 0.75,
        px: 0.75,
      }}
    >
      {groups.map((group) => (
        <MeetingTreeGroup
          key={group.customerName || "__none__"}
          group={group}
          expanded={expandedKeys.has(group.customerName)}
          onToggle={() => toggleGroup(group.customerName)}
          selectedId={selectedId}
          t={t}
          theme={theme}
          locale={dateLocale}
          onSelect={selectMeeting}
          onOpen={openMeeting}
        />
      ))}
    </Paper>
  );

  const previewPane = selected ? (
    <Paper
      sx={{ ...pageContentCardSx(theme), p: { xs: 2, md: 2.5 } }}
      elevation={0}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                lineHeight: 1.25,
                wordBreak: "break-word",
              }}
            >
              {selected.title || t("Meetings.untitled")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.35 }}
            >
              {[
                formatMeetingListDate(selected.meetingDate, dateLocale) ||
                  selected.meetingDate,
                selected.customerName || t("Meetings.noCustomer"),
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          {canDelete ? (
            <IconButton
              size="small"
              color="error"
              aria-label={t("Meetings.delete")}
              onClick={() => setDeleteTarget(selected)}
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Stack>
        {(selected.attendeeNames || selected.clientContactNames) && (
          <Typography variant="caption" color="text.secondary">
            {[selected.attendeeNames, selected.clientContactNames]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        )}
        <Box sx={meetingChipRowSx}>
          <Chip
            size="small"
            color={statusColor[selected.status] || "default"}
            label={t(statusLabelKey(selected.status))}
          />
          <Chip
            size="small"
            variant="outlined"
            color={
              selected.mediaPurged || previewDays === 0
                ? "default"
                : previewDays <= 7
                  ? "warning"
                  : "info"
            }
            label={
              selected.mediaPurged
                ? t("Meetings.mediaPurgedChip")
                : t("Meetings.mediaDaysLeftChip", {
                    days: previewDays ?? "—",
                  })
            }
          />
          {!hasMeetingConsent(selected) ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("Meetings.consentMissingChip")}
            />
          ) : null}
        </Box>
        <Typography variant="body2">
          {t("Meetings.counts", {
            audio: selected.audioCount || 0,
            photos: selected.imageCount ?? selected.photoCount ?? 0,
            videos: selected.videoCount || 0,
          })}
        </Typography>
        {previewHeadline ? (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, textTransform: "uppercase" }}
            >
              {t("Meetings.summary")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, whiteSpace: "pre-wrap", lineHeight: 1.45 }}
            >
              {previewHeadline.length > 420
                ? `${previewHeadline.slice(0, 420)}…`
                : previewHeadline}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("Meetings.previewNoSummary")}
          </Typography>
        )}
        <Button
          variant="contained"
          size="large"
          startIcon={<OpenInNewOutlinedIcon />}
          onClick={() => openMeeting(selected)}
          sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minHeight: 48 }}
        >
          {t("Meetings.openMeeting")}
        </Button>
      </Stack>
    </Paper>
  ) : (
    <Paper sx={pageContentCardSx(theme)} elevation={0}>
      <Typography color="text.secondary">
        {t("Meetings.previewEmpty")}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={meetingPageShellSx(contentMargin)}>
      <Box
        sx={{
          ...meetingStickyHeaderSx(theme),
          flexShrink: 0,
          pb: 1.25,
        }}
      >
        <Paper
          sx={{
            ...pageHeroShellSx(theme),
            flexShrink: 0,
            mb: 0,
          }}
          elevation={0}
        >
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.75, sm: 2.25 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={1.75}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: "primary.main",
                    flexShrink: 0,
                  }}
                >
                  <MicNoneOutlinedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, lineHeight: 1.2 }}
                  >
                    {t("Meetings.title")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.35, lineHeight: 1.4 }}
                  >
                    {t("Meetings.subtitle")}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddOutlinedIcon />}
                onClick={openCreate}
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                  minHeight: 44,
                  px: 2.25,
                  fontWeight: 800,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {t("Meetings.new")}
              </Button>
            </Stack>
          </Box>
          <Alert
            severity="warning"
            sx={{
              borderRadius: 0,
              borderTop: "1px solid",
              borderColor: "divider",
              py: 0.5,
              px: { xs: 2, sm: 2.5 },
              bgcolor: alpha(theme.palette.warning.light, 0.35),
              "& .MuiAlert-message": {
                py: 0.25,
                fontSize: "0.8125rem",
                lineHeight: 1.35,
              },
              "& .MuiAlert-icon": { py: 0.5, mr: 1 },
            }}
          >
            {t("Meetings.retentionBanner", {
              days: MEETING_MEDIA_RETENTION_DAYS,
              warnMb: formatMeetingMediaWarnMb(),
              maxMb: formatMeetingMediaMaxMb(),
            })}
          </Alert>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            sx={{ mt: 1.25, mb: 0 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        ) : null}

        {!loading && rows.length > 0 ? filtersBar : null}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={pageContentCardSx(theme)} elevation={0}>
          <Typography color="text.secondary">{t("Meetings.empty")}</Typography>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            sx={{ mt: 2, minHeight: 48 }}
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            {t("Meetings.new")}
          </Button>
        </Paper>
      ) : filtered.length === 0 ? (
        <Paper sx={pageContentCardSx(theme)} elevation={0}>
          <Typography color="text.secondary">
            {t("Meetings.filterEmpty")}
          </Typography>
        </Paper>
      ) : isDesktop ? (
        <Box sx={meetingListSplitSx}>
          {listPane}
          <Box sx={meetingPreviewPaneSx}>{previewPane}</Box>
        </Box>
      ) : (
        listPane
      )}

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        fullScreen={isNarrow}
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>{t("Meetings.new")}</DialogTitle>
        <DialogContent dividers={isNarrow} sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Stack spacing={1.5} sx={{ pt: 0.5, minWidth: 0 }}>
            <MeetingBrandSelect
              autoFocus
              value={customerName}
              onChange={(name) => {
                setCustomerName(name);
                setClientContactIds([]);
              }}
              disabled={saving}
            />
            <TextField
              type="date"
              label={t("Meetings.date")}
              fullWidth
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
            <MeetingAttendeeSelect
              value={attendeeIds}
              onChange={setAttendeeIds}
              disabled={saving}
            />
            <MeetingClientContactSelect
              brandName={customerName}
              value={clientContactIds}
              onChange={setClientContactIds}
              disabled={saving}
            />
            <TextField
              label={t("Meetings.sessionTitle")}
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("Meetings.titlePlaceholder")}
            />
            <MeetingConsentPanel
              value={consent}
              onChange={setConsent}
              disabled={saving}
              dense
            />
            <Alert severity="info">
              {t("Meetings.retentionBanner", {
                days: MEETING_MEDIA_RETENTION_DAYS,
                warnMb: formatMeetingMediaWarnMb(),
                maxMb: formatMeetingMediaMaxMb(),
              })}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button
            fullWidth={isNarrow}
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            {t("Meetings.cancel")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !isMeetingConsentComplete(consent)}
            sx={{ minHeight: 48 }}
          >
            {saving ? <CircularProgress size={18} /> : t("Meetings.start")}
          </Button>
        </DialogActions>
      </Dialog>

      <MeetingDeleteDialog
        open={Boolean(deleteTarget)}
        session={deleteTarget}
        deleting={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
